import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../redux/store';
import { setPermissions } from '../redux/slice/userSlice';
import { GET } from '../utils/apiRequest';

export const usePermissions = () => {
    const dispatch = useDispatch();
    const { user } = useSelector((state: RootState) => state.User);

    const fetchPermissions = useCallback(async () => {
        if (!user?.roleId) return;

        const response = await GET('/admin/roles/my-permissions');
        if (response.status) {
            dispatch(setPermissions(response.data?.data || response.data || []));
        }
    }, [user?.roleId, dispatch]);

    const hasPermission = useCallback((module: string, action: 'view' | 'create' | 'edit' | 'delete' | 'publish') => {
        if (!user) return false;

        // Admins have all permissions
        if (user.role === 'SUPER_ADMIN' || user.role === 'INSTITUTION_ADMIN') {
            return true;
        }

        // Judges inherently have scoring permissions
        if (user.role === 'JUDGE' && module === 'scoring') {
            return true;
        }

        // Team leads have full participants permissions (backend bypasses permission check for this module)
        if (user.role === 'TEAM_LEAD' && module === 'participants') {
            return true;
        }

        if (!user.permissions) return false;

        // Alias certificate_templates and poster_templates → certificates
        // (the DB and PermissionMatrix store this functionality under the 'certificates' module key)
        const resolvedModule =
            module === 'certificate_templates' || module === 'poster_templates'
                ? 'certificates'
                : module;

        const modulePermission = user.permissions.find(p => p.module === resolvedModule);
        if (!modulePermission) return false;

        return modulePermission[action] === true;
    }, [user]);

    return { fetchPermissions, hasPermission, permissions: user?.permissions };
};
